import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const Vitals = ({ user, onLogout }) => {
  const location = useLocation();
  const [vitals, setVitals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    heart_rate: '',
    temperature: '',
    blood_sugar: '',
    weight: '',
    oxygen_saturation: '',
    feeling: '',
    notes: ''
  });

  const fetchVitals = async () => {
    try {
      const [vitalsRes, statsRes] = await Promise.all([
        axios.get(`${API}/vitals?limit=30`),
        axios.get(`${API}/vitals/stats/summary`)
      ]);
      setVitals(vitalsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load vitals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVitals();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        blood_pressure_systolic: formData.blood_pressure_systolic ? parseInt(formData.blood_pressure_systolic) : null,
        blood_pressure_diastolic: formData.blood_pressure_diastolic ? parseInt(formData.blood_pressure_diastolic) : null,
        heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        blood_sugar: formData.blood_sugar ? parseInt(formData.blood_sugar) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        oxygen_saturation: formData.oxygen_saturation ? parseInt(formData.oxygen_saturation) : null,
        feeling: formData.feeling || null,
        notes: formData.notes || null
      };

      await axios.post(`${API}/vitals`, dataToSubmit);
      toast.success('Vitals recorded successfully');
      setFormData({
        blood_pressure_systolic: '',
        blood_pressure_diastolic: '',
        heart_rate: '',
        temperature: '',
        blood_sugar: '',
        weight: '',
        oxygen_saturation: '',
        feeling: '',
        notes: ''
      });
      setShowAddForm(false);
      fetchVitals();
    } catch (error) {
      toast.error('Failed to record vitals');
    }
  };

  const handleDelete = async (vitalId) => {
    if (window.confirm('Delete this vitals entry?')) {
      try {
        await axios.delete(`${API}/vitals/${vitalId}`);
        toast.success('Entry deleted');
        fetchVitals();
      } catch (error) {
        toast.error('Failed to delete entry');
      }
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getFeelingEmoji = (feeling) => {
    const emojis = {
      'great': '😊',
      'good': '🙂',
      'okay': '😐',
      'tired': '😴',
      'unwell': '🤒',
      'stressed': '😰'
    };
    return emojis[feeling] || '📝';
  };

  return (
    <div className="dashboard-container" data-testid="vitals-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>My Vitals & Wellness</h1>
          <button 
            className="submit-button" 
            style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
            onClick={() => setShowAddForm(!showAddForm)}
            data-testid="add-vitals-toggle"
          >
            {showAddForm ? 'Cancel' : '+ Record Vitals'}
          </button>
        </div>

        {/* Stats Summary */}
        {stats && stats.total_entries > 0 && (
          <div className="section-card" style={{ marginBottom: '2rem' }} data-testid="vitals-stats">
            <h2 className="section-title">30-Day Summary</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
              {stats.blood_pressure?.avg_systolic && (
                <div style={{ textAlign: 'center', padding: '1rem', background: '#e7f3ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5364' }}>
                    {stats.blood_pressure.avg_systolic}/{stats.blood_pressure.avg_diastolic}
                  </div>
                  <div style={{ color: '#6b8899', fontSize: '0.9rem' }}>Avg Blood Pressure</div>
                </div>
              )}
              {stats.heart_rate?.avg && (
                <div style={{ textAlign: 'center', padding: '1rem', background: '#ffe7e7', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#d9534f' }}>
                    {stats.heart_rate.avg} <span style={{ fontSize: '0.8rem' }}>bpm</span>
                  </div>
                  <div style={{ color: '#6b8899', fontSize: '0.9rem' }}>Avg Heart Rate</div>
                </div>
              )}
              {stats.weight?.latest && (
                <div style={{ textAlign: 'center', padding: '1rem', background: '#e7ffe7', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                    {stats.weight.latest} <span style={{ fontSize: '0.8rem' }}>lbs</span>
                  </div>
                  <div style={{ color: '#6b8899', fontSize: '0.9rem' }}>Latest Weight</div>
                </div>
              )}
              {stats.oxygen_saturation?.avg && (
                <div style={{ textAlign: 'center', padding: '1rem', background: '#fff3cd', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>
                    {stats.oxygen_saturation.avg}%
                  </div>
                  <div style={{ color: '#6b8899', fontSize: '0.9rem' }}>Avg O₂ Sat</div>
                </div>
              )}
              <div style={{ textAlign: 'center', padding: '1rem', background: '#f0f0f0', borderRadius: '8px' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5364' }}>
                  {stats.total_entries}
                </div>
                <div style={{ color: '#6b8899', fontSize: '0.9rem' }}>Total Entries</div>
              </div>
            </div>
          </div>
        )}

        {/* Add Vitals Form */}
        {showAddForm && (
          <div className="section-card" style={{ marginBottom: '2rem' }} data-testid="vitals-form">
            <h2 className="section-title">Record New Vitals</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group">
                  <label>Blood Pressure (Systolic/Diastolic)</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="number"
                      name="blood_pressure_systolic"
                      value={formData.blood_pressure_systolic}
                      onChange={handleChange}
                      placeholder="120"
                      min="60"
                      max="250"
                      data-testid="bp-systolic-input"
                    />
                    <span>/</span>
                    <input
                      type="number"
                      name="blood_pressure_diastolic"
                      value={formData.blood_pressure_diastolic}
                      onChange={handleChange}
                      placeholder="80"
                      min="40"
                      max="150"
                      data-testid="bp-diastolic-input"
                    />
                    <span style={{ color: '#6b8899' }}>mmHg</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="heart_rate">Heart Rate</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="heart_rate"
                      type="number"
                      name="heart_rate"
                      value={formData.heart_rate}
                      onChange={handleChange}
                      placeholder="72"
                      min="30"
                      max="220"
                      data-testid="heart-rate-input"
                    />
                    <span style={{ color: '#6b8899' }}>bpm</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="temperature">Temperature</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="temperature"
                      type="number"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleChange}
                      placeholder="98.6"
                      min="95"
                      max="108"
                      step="0.1"
                      data-testid="temperature-input"
                    />
                    <span style={{ color: '#6b8899' }}>°F</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="blood_sugar">Blood Sugar</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="blood_sugar"
                      type="number"
                      name="blood_sugar"
                      value={formData.blood_sugar}
                      onChange={handleChange}
                      placeholder="100"
                      min="20"
                      max="600"
                      data-testid="blood-sugar-input"
                    />
                    <span style={{ color: '#6b8899' }}>mg/dL</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="weight">Weight</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="weight"
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="150"
                      min="50"
                      max="500"
                      step="0.1"
                      data-testid="weight-input"
                    />
                    <span style={{ color: '#6b8899' }}>lbs</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="oxygen_saturation">Oxygen Saturation</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="oxygen_saturation"
                      type="number"
                      name="oxygen_saturation"
                      value={formData.oxygen_saturation}
                      onChange={handleChange}
                      placeholder="98"
                      min="70"
                      max="100"
                      data-testid="o2-sat-input"
                    />
                    <span style={{ color: '#6b8899' }}>%</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="feeling">How are you feeling?</label>
                <select
                  id="feeling"
                  name="feeling"
                  value={formData.feeling}
                  onChange={handleChange}
                  data-testid="feeling-select"
                >
                  <option value="">Select...</option>
                  <option value="great">😊 Great</option>
                  <option value="good">🙂 Good</option>
                  <option value="okay">😐 Okay</option>
                  <option value="tired">😴 Tired</option>
                  <option value="unwell">🤒 Unwell</option>
                  <option value="stressed">😰 Stressed</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="3"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any additional notes about your wellness today..."
                  data-testid="vitals-notes-input"
                />
              </div>

              <button type="submit" className="submit-button" data-testid="save-vitals-button">
                Save Vitals Entry
              </button>
            </form>
          </div>
        )}

        {/* Vitals History */}
        <div className="section-card">
          <h2 className="section-title">Recent Entries</h2>
          
          {loading ? (
            <p>Loading vitals...</p>
          ) : vitals.length === 0 ? (
            <div className="empty-state" data-testid="empty-vitals">
              <div className="empty-state-icon">💓</div>
              <h3>No Vitals Recorded Yet</h3>
              <p style={{ color: '#6b8899' }}>
                Track your health by recording your vitals regularly. This helps monitor your wellbeing as a caregiver.
              </p>
              <button 
                className="submit-button" 
                style={{ marginTop: '1rem', width: 'auto', padding: '0.75rem 2rem' }}
                onClick={() => setShowAddForm(true)}
              >
                Record Your First Entry
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {vitals.map((vital) => (
                <div 
                  key={vital.id} 
                  style={{ 
                    padding: '1rem', 
                    background: '#f8f9fa', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #2c5364'
                  }}
                  data-testid={`vital-entry-${vital.id}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{getFeelingEmoji(vital.feeling)}</span>
                      <span style={{ color: '#6b8899', fontSize: '0.9rem' }}>{formatDate(vital.timestamp)}</span>
                    </div>
                    <button
                      className="delete-button"
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => handleDelete(vital.id)}
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {vital.blood_pressure_systolic && (
                      <span style={{ background: '#e7f3ff', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        🩺 {vital.blood_pressure_systolic}/{vital.blood_pressure_diastolic} mmHg
                      </span>
                    )}
                    {vital.heart_rate && (
                      <span style={{ background: '#ffe7e7', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        ❤️ {vital.heart_rate} bpm
                      </span>
                    )}
                    {vital.temperature && (
                      <span style={{ background: '#fff3cd', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        🌡️ {vital.temperature}°F
                      </span>
                    )}
                    {vital.blood_sugar && (
                      <span style={{ background: '#e7ffe7', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        🍬 {vital.blood_sugar} mg/dL
                      </span>
                    )}
                    {vital.weight && (
                      <span style={{ background: '#f0e7ff', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        ⚖️ {vital.weight} lbs
                      </span>
                    )}
                    {vital.oxygen_saturation && (
                      <span style={{ background: '#e7fff3', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>
                        💨 {vital.oxygen_saturation}% O₂
                      </span>
                    )}
                  </div>
                  
                  {vital.notes && (
                    <p style={{ margin: '0.75rem 0 0', color: '#4a6572', fontStyle: 'italic' }}>
                      {vital.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Vitals;
